import React, { useRef, useState } from "react";
import { IconButton, Tooltip, Menu, MenuItem, Typography } from "@/components/ui";
import { FaGripHorizontal, FaList, FaTable, FaColumns } from "react-icons/fa";
import useTranslation from "@/hooks/use-translation";

export type TrackerViewMode = "gallery" | "list" | "table" | "board";

interface TrackerViewSelectorProps {
  currentView: TrackerViewMode;
  onViewChange: (view: TrackerViewMode) => void;
  className?: string;
}

const TrackerViewSelector: React.FC<TrackerViewSelectorProps> = ({
  currentView,
  onViewChange,
  className = "",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleViewSelect = (view: TrackerViewMode) => {
    onViewChange(view);
    handleClose();
  };

  const viewOptions = [
    {
      value: "gallery" as TrackerViewMode,
      label: "Galerie",
      icon: <FaGripHorizontal className="w-4 h-4" />,
      description: "Vue en grille avec cartes",
    },
    {
      value: "list" as TrackerViewMode,
      label: "Liste",
      icon: <FaList className="w-4 h-4" />,
      description: "Vue en liste compacte",
    },
    {
      value: "table" as TrackerViewMode,
      label: "Tableau",
      icon: <FaTable className="w-4 h-4" />,
      description: "Vue en tableau avec colonnes",
    },
    {
      value: "board" as TrackerViewMode,
      label: "Kanban",
      icon: <FaColumns className="w-4 h-4" />,
      description: "Vue Kanban par statut",
    },
  ];

  const currentViewOption = viewOptions.find(
    (option) => option.value === currentView
  );

  return (
    <div className={`relative ${className}`}>
      <Tooltip content="Changer la vue">
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
          aria-controls={open ? "view-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          size="sm"
          variant="ghost"
        >
          {currentViewOption?.icon}
        </IconButton>
      </Tooltip>

      <Menu
        open={open}
        onClose={handleClose}
        anchorEl={buttonRef.current}
      >
        {viewOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleViewSelect(option.value)}
            className={`min-w-[200px] ${currentView === option.value ? "bg-accent-muted" : ""}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-content-secondary">{option.icon}</span>
              <div className="flex flex-col">
                <Typography variant="text-sm" className="font-medium">
                  {option.label}
                </Typography>
                <Typography variant="text-xs" color="secondary">
                  {option.description}
                </Typography>
              </div>
            </div>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default TrackerViewSelector;
